import { useState, useEffect, useContext } from 'react';
import { ScenariosContext } from '../context/scenarios-context';

/**
 * Hook for accessing scenarios from context
 * Uses shared cache to prevent duplicate API calls
 * 
 * @param {string} projectId - Project ID
 * @param {string} token - JWT token
 * @returns {object} { scenarios, loading, error, refetchScenarios }
 */
export const useScenarios = (projectId, token) => {
  const { scenariosByProject, loading, error, fetchScenarios, refetchScenarios: contextRefetch } = useContext(ScenariosContext);
  const [localScenarios, setLocalScenarios] = useState([]);

  useEffect(() => {
    const loadScenarios = async () => {
      if (!projectId || !token) {
        setLocalScenarios([]);
        return;
      }

      // Check if scenarios already cached
      if (scenariosByProject[projectId]) {
        setLocalScenarios(scenariosByProject[projectId]);
      } else {
        // Fetch from API (will be cached in context)
        const scenarios = await fetchScenarios(projectId, token);
        setLocalScenarios(scenarios);
      }
    };

    loadScenarios();
  }, [projectId, token, scenariosByProject, fetchScenarios]);

  // Wrapper for refetch that updates local state
  const refetchScenarios = async () => {
    if (!projectId || !token) return;
    const scenarios = await contextRefetch(projectId, token);
    setLocalScenarios(scenarios);
  };

  return { 
    scenarios: localScenarios, 
    loading, 
    error, 
    refetchScenarios 
  };
};
